import cx from 'classnames';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSelector } from 'react-redux';
import {
  apiEditProductV3,
  apiGetPublicUUID,
  apiGetPublicUUIDTexture,
  apiPostDataToRaindrop,
  GetProductShareStatus,
  GetTextureShareStatus,
  MakeProductPublicShare,
  MakeTexturePublicShare,
} from '../../helpers/api';
import { useMemo } from 'react';
import { PRODUCT_FILTER_TYPE, TEXTURE_FILTER_TYPE } from '../../constants/constants';
import { useFilterDropdownOptions } from '../../hooks/useFilterDropdownOptions';
import {
  dimensionsAllSchema,
  getDescriptionFields,
  getLiveLinksFields,
  getCategoryFields,
  getDimensionsFields,
  getDimensionsXFields,
  getDimensionsYFields,
  getDimensionsZFields,
  getNameFields,
  getPriceFields,
  getSkuFields,
  nameSchema,
} from '../../common/PropDetailsPopup/fieldsConfig';
import { EditSection } from '../../common/EditSection/EditSection';
import { Form } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { EditPopupUI } from '../../common/EditPopupUI/EditPopupUI';
import { errorToast } from '../../helpers/toastHelper';
import { EditPopupButtonsUI } from '../../common/EditPopupUI/EditPopupButtonsUI';
import { CloseIcon } from '../../assets/icons';
import { CONVERT_TO_CM, MEASURE_UNIT_LABEL } from '../../config/constant/unityConstants';
import { selectActiveMeasureUnitsName } from '../../redux/slicers/admin/curatorSlicer';
import { convertActiveUnitToCM, convertCMToActiveUnits } from '../../helpers/curatorActions';
import { decimals, parseFloatNaN } from '../../helpers/jsHelper';
import {
  getHeightFields,
  getRotationFields,
  getWidthFields,
} from '../../common/Textures/fieldsConfig';
import { Image } from '../../common';
import { useUserContext } from '../../contexts/UserContext';
import { handleCopyURL } from '../../common/SharePopup';
import { LogConsole } from '../../helpers/logEvents';

const createPublicPage = async ({ id = null, isTexture = false, callback = null }) => {
  if (!id) return null;
  const payload = {
    [isTexture ? 'texture' : 'product']: id,
    is_publish: true,
    is_public: true,
  };

  const api = isTexture ? MakeTexturePublicShare : MakeProductPublicShare;
  const response = await api(payload);

  if (response?.status && response?.data) {
    window.$shareItem = response?.data;
    window.$shareFlag = response?.data?.is_public;
    return await callback?.();
  }

  return null;
};

const getPublicPageUnicode = async ({ id = null, isTexture = false }) => {
  if (!id) return null;
  const getFunc = isTexture ? GetTextureShareStatus : GetProductShareStatus;
  const res = await getFunc(id);

  if (res?.results?.length > 0) { // && res?.results?.[0]?.is_public) {
    // if product page is public or not but, we get the results. we are sending it's data to the raindrop
    const getPublicUUID = isTexture ? apiGetPublicUUIDTexture : apiGetPublicUUID;
    const response = await getPublicUUID(`?${isTexture ? 'texture' : 'product'}=${id}`);
    if (response?.results?.length) return response.results[0]?.unique_code;
    return null;
  } else if (res?.results?.length === 0) {
    // If no results were found, creating the public page
    return await createPublicPage({
      id,
      isTexture,
      callback: () => getPublicPageUnicode({ id, isTexture }), // Callback to retry fetching the public page Unicode after making it public
    });
  }
  return null;
};

export const EditProductTextureDetailsPopupV2 = ({
  className = '',
  title = 'Edit details',
  productId,
  data: _data,
  show,
  onClose,
  loading,
  thumbnail,
  onUpdateSuccess,
  saveApi = apiEditProductV3,
  isTexture = false,
  placement = 'end',
}) => {
  const measureUnits = useSelector(selectActiveMeasureUnitsName);
  const DEFAULT_VALUE = decimals(convertCMToActiveUnits(CONVERT_TO_CM), 2);

  const parseTextureSize = (item) => {
    return decimals(convertCMToActiveUnits(parseFloatNaN(item || DEFAULT_VALUE)), 2);
  };

  const data = useMemo(() => {
    if (!_data) return _data;

    if (isTexture) {
      return {
        ..._data,
        width: parseTextureSize(_data.width),
        height: parseTextureSize(_data.height),
        rotation: parseInt(_data.rotation),
      };
    }

    return _data;
  }, [_data]);

  const { sku_id } = data || {};
  const { isGuestUser, isEditor, currentUser } = useUserContext();

  const _productFilters = useFilterDropdownOptions({
    filterType: isTexture ? TEXTURE_FILTER_TYPE : PRODUCT_FILTER_TYPE,
    fetchData: true,
  });

  // showing only the product page filter, and hiding other categories filter as we are not showing them earlier on product page
  const productFilters = useMemo(() => {
    // 'category' & 'categories' are the category in the product & texture filters respectively
    const categoryFilter = _productFilters?.find((a) =>
      ['category', 'categories'].includes(a?.label_name?.trim()?.toLowerCase())
    );

    if (isEditor || isGuestUser())
      return [
        ...(categoryFilter ? [categoryFilter] : []),
        ..._productFilters?.filter((a) => a?.is_product_page_filter),
      ];
    return [];
  }, [_productFilters, isGuestUser, isEditor]);

  const getFilterValue = (filterId, _data = data) => {
    if (!_data?.filteroptions || !productFilters) return [];

    const ids = _data.filteroptions.map((o) => o.id);
    const filter = productFilters?.find((filter) => filter.id === filterId);
    const value = filter?.options?.filter((category) => ids.includes(category.value));
    return value;
  };

  //   const memoizedSearch = useCallback(async (search, page) => {
  //     const response = await apiGetGlobalTags({ search, page });

  //     return {
  //       options: response.results.map(getSearchTagOption),
  //       hasMore: Boolean(response.next_link),
  //       additional: {
  //         page: page + 1,
  //       },
  //     };
  //   }, []);

  //   const loadSearchTagOptions = async (search, loadedOptions, { page }) => {
  //     return memoizedSearch(search, page);
  //   };

  //   const tagsValue = data?.tags.map(getSearchTagOption);

  //   const handleCreateTag = async (newTagName) => {
  //     const val = newTagName.trim();
  //     const result = await dispatch(createGlobalTagAction({ name: val }));
  //     if (!result?.data?.id) return;

  //     const existingTags = getValues('tags');
  //     const existingValues = existingTags.map((a) => a.value);

  //     setValue('tags', [...existingValues, result?.data?.name].map(getSearchTagOption));
  //   };

  const {
    nameFields,
    skuFields,
    priceFields,
    descriptionFields,
    liveLinksFields,
    // dimensionsFields, // for product page
    dimensionsXFields, // for product page
    dimensionsYFields, // for product page
    dimensionsZFields, // for product page

    widthField, // for texture page
    heightField, // for texture page
    rotationField, // for texture page

    // tagsFieldsConfig,
    defaultValues,
    schema,
  } = useMemo(() => {
    if (!data?.id) return {};

    let schemaObj = {
      ...nameSchema,
      ...dimensionsAllSchema,
    };

    const nameFields = getNameFields(data);
    const skuFields = getSkuFields(data);
    const priceFields = getPriceFields(data);
    const descriptionFields = getDescriptionFields(data);
    const liveLinksFields = getLiveLinksFields(data);
    // const dimensionsFields = getDimensionsFields(data);
    const dimensionsXFields = getDimensionsXFields(data);
    const dimensionsYFields = getDimensionsYFields(data);
    const dimensionsZFields = getDimensionsZFields(data);

    const widthField = getWidthFields(data);
    const heightField = getHeightFields(data);
    const rotationField = getRotationFields(data);

    // const tagsFieldsConfig = getTagsFields(tagsValue, loadSearchTagOptions, handleCreateTag);

    const fields = [
      ...nameFields,
      ...skuFields,
      ...priceFields,
      ...descriptionFields,
      ...liveLinksFields,
      ...(isTexture
        ? [...widthField, ...heightField, ...rotationField]
        : [
            // ...dimensionsFields
            ...dimensionsXFields,
            ...dimensionsYFields,
            ...dimensionsZFields,
          ]),
      //   ...tagsFieldsConfig.fieldsConfig,
    ];

    productFilters.map((filter) => {
      const { fieldsConfig, schema } = getCategoryFields(filter, getFilterValue(filter.id));
      fields.push(...fieldsConfig);
      schemaObj = { ...schemaObj, ...schema };
    });

    // fields.push(...tagsFieldsConfig.fieldsConfig)
    // schemaObj = { ...schemaObj, ...tagsFieldsConfig.schema };

    const defaultValues = fields.reduce((acc, field) => {
      if (!field.name) return acc;

      acc[field.name] = field.value;
      return acc;
    }, {});

    return {
      nameFields,
      skuFields,
      priceFields,
      descriptionFields,
      liveLinksFields,
      // dimensionsFields,
      dimensionsXFields,
      dimensionsYFields,
      dimensionsZFields,

      widthField,
      heightField,
      rotationField,
      //   tagsFieldsConfig,
      defaultValues,
      schema: yup.object(schemaObj),
    };
  }, [data, data.tags, productFilters]);

  const methods = useForm({
    resolver: schema ? yupResolver(schema) : undefined,
    defaultValues,
    mode: 'onChange',
  });

  const {
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { isSubmitting, isDirty },
  } = methods;

  // useEffect(() => {
  //   reset(defaultValues);
  // }, [defaultValues])

  const onInvalid = () => {
    errorToast('Please fix errors', { toastId: 'TOAST_FIX_ERRORS' });
  };

  const onSubmit = async (values) => {
    if (!isDirty) {
      onClose();
      return;
    }

    const updatedData = { filteroptions: [], tags: [] };

    Object.keys(values).forEach((key) => {
      if (key.startsWith('filter_')) {
        const options = values[key];
        if (options) updatedData.filteroptions.push(...options.map((c) => c.value));
      } else if (key === 'tags') {
        const options = values[key];
        if (options) updatedData.tags.push(...options.map((c) => c.value));
      } else if (key === 'width' || key === 'height') {
        updatedData[key] = convertActiveUnitToCM(values[key], 2);
      } else {
        const v = typeof values[key] === 'string' ? values[key]?.trim() : values[key];
        updatedData[key] = v || '';
      }
    });

    const response = await saveApi({
      id: data.id,
      ...updatedData,
      ...(isGuestUser() ? { is_guest_user: true } : {}),
    });

    if (response) {
      if (isEditor || isGuestUser()) {
        // here, only the internal user can send the data to the raindrop
        const id = response?.id || data?.id;
        const raindropId = response?.raindrop_id || data?.raindrop_id;
        const liveLinks = response?.live_links || data?.live_links;

        // Get the unique code for the public page
        const uniqueCode = await getPublicPageUnicode({ id: data?.id, isTexture });

        if (uniqueCode) {
          // Generate the public link using the unique code
          const publicLink = handleCopyURL({
            productId: id,
            productUnicode: uniqueCode,
            isV2: true,
            isTexture,
            disableCopy: true,
            showMessage: false,
          });

          const _response = isTexture ? response?.data : response;

          const tags = _productFilters
            ?.map((f) => getFilterValue(f.id, _response))
            ?.filter(Boolean)
            ?.flat()
            ?.map((a) => a?.label?.trim());

          const ownerOrganizationName =
            _response?.owner_organization === currentUser?.organization
              ? currentUser?.organization_detail?.name
              : '';

          const _tags = [ownerOrganizationName || '', 'Product Page', ...tags].filter(Boolean);

          const raindropResponse = await apiPostDataToRaindrop({
            data: {
              ...(isTexture ? { texture: id } : { product: id }),
              title: _response?.name,
              link: publicLink, // this will be the public product page link
              tags: _tags?.join(','),
              cover: _response?.thumbnail,
              ...(liveLinks ? { note: liveLinks } : {}), // sending live_links
              ...(raindropId
                ? {
                    raindrop_id: raindropId,
                  }
                : {}),
            },
            updateData: raindropId,
          });

          // showing error to the internal user. so, that they inform to the admin or the developer.
          if (!raindropResponse?.result) {
            errorToast('Error saving data on Raindrop. Please contact Admin.');
          }

          LogConsole('DEBUG___raindropResponse', raindropResponse);
        }
      }
      onUpdateSuccess?.(response);
    }
  };

  let showXlabelFirst = false;

  if (dimensionsXFields?.[0]?.value >= dimensionsZFields?.[0]?.value) {
    showXlabelFirst = true;
  } else {
    showXlabelFirst = false;
  }

  return (
    <EditPopupUI
      showDefaultHeader={true}
      showCustomHeader={false}
      hideThumbnail={true}
      show={show}
      onClose={onClose}
      loading={loading}
      title={title}
      customCloseIcon={CloseIcon}
      className={cx('rightSidebar leaveHeader product-details', className)}
      backdropClassName="hideBackdrop"
      placement={placement}>
      {!loading && data && (
        <FormProvider {...methods}>
          <Form onSubmit={handleSubmit(onSubmit, onInvalid)} autoComplete="off">
            <p className="image-title">Thumbnail</p>
            <div className="image-part">
              <Image
                src={thumbnail}
                ratio={1.4}
                placeholderWhileLoading
                hideWithNoSrc
                alt={data?.name}
                style={isTexture ? { transform: `rotate(${parseFloat(data?.rotation)}deg)` } : {}}
              />
            </div>
            {/* name */}
            <EditSection title="Product name" fields={nameFields} designV2={true} />

            {/* Description */}
            <EditSection title="Description" fields={descriptionFields} designV2={true} />

            {/* sku */}
            <EditSection title="SKU ID" fields={skuFields} designV2={true} />
            {isTexture ? (
              <>
                <EditSection
                  title={`Width (${MEASURE_UNIT_LABEL[measureUnits] || ''}) *`}
                  fields={widthField}
                  designV2={true}
                />

                <EditSection
                  title={`Height (${MEASURE_UNIT_LABEL[measureUnits] || ''}) *`}
                  fields={heightField}
                  designV2={true}
                />

                <EditSection title="Rotation" fields={rotationField} designV2={true} />
              </>
            ) : (
              <>
                {/* price */}
                <EditSection title="Price" fields={priceFields} designV2={true} />

                {/* Dimensions */}
                {/* <EditSection title="Dimensions" fields={dimensionsFields} designV2={true} /> */}

                <EditSection
                  title={`Length (Inches)`}
                  fields={showXlabelFirst ? dimensionsXFields : dimensionsZFields}
                  designV2={true}
                />

                <EditSection
                  title={`Width (Inches)`}
                  fields={showXlabelFirst ? dimensionsZFields : dimensionsXFields}
                  designV2={true}
                />

                <EditSection title={`Height (Inches)`} fields={dimensionsYFields} designV2={true} />
              </>
            )}
            {/* filters */}
            {productFilters.map((filter) => {
              const { fieldsConfig } = getCategoryFields(filter, getFilterValue(filter.id));
              return (
                <EditSection
                  key={filter.id}
                  title={filter.label_name}
                  fields={fieldsConfig}
                  previewMode="fields"
                  designV2={true}
                />
              );
            })}

            {isEditor || isGuestUser() ? (
              <>
                {/* Live links */}
                <EditSection
                  title="Live Links ( comma separated )"
                  fields={liveLinksFields}
                  designV2={true}
                />
              </>
            ) : null}

            {/* tags */}
            {/* <EditSection title="Tags" fields={tagsFieldsConfig.fieldsConfig} previewMode="fields" /> */}
            <EditPopupButtonsUI
              loading={isSubmitting}
              style={{
                padding: '0.75rem 1rem',
              }}
              buttonStyle={{
                height: '40px',
              }}
              hideCancelButton={true}
              // onCacel={onClose}
            />
          </Form>
        </FormProvider>
      )}
    </EditPopupUI>
  );
};
